import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  Download,
  Edit2,
  EyeOff,
  Heart,
  Loader2,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminItemImage } from "./AdminItemImage";
import { ScheduleTag } from "@/components/ScheduleTag";
import { API } from "./admin-utils";

const csvEscape = (value) => {
  const str = String(value ?? "");
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadLikesMatrixCsv = async () => {
  try {
    const res = await axios.get(`${API}/admin/likes-matrix`);
    const { locations, items } = res.data;
    const locationCols = locations.map((l) => l.name);
    const header = ["Item", "Category", "Total", ...locationCols];
    const rows = items.map((it) => [
      it.name,
      it.category_label,
      it.total,
      ...locations.map((l) => it.counts[l.slug] || 0),
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");

    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `munchd-likes-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} items × ${locations.length} locations`);
  } catch {
    toast.error("Failed to export likes");
  }
};

export function MenuItemsTab({
  loading,
  items,
  categories,
  selectedCategoryFilter,
  onSelectCategoryFilter,
  reordering,
  onMoveItem,
  onEditItem,
  onDeleteItem,
  onViewLikes,
  onBulkUpdated,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState(null); // "price" | "schedule" | null
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkSchedule, setBulkSchedule] = useState("all_week");
  const [bulkSaving, setBulkSaving] = useState(false);
  // Archive view: "active" (default), "archived", "all"
  const [archiveView, setArchiveView] = useState("active");
  const [rowSavingId, setRowSavingId] = useState(null);

  // Clear selection when the visible list changes materially (category filter or archive view)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedCategoryFilter, archiveView]);

  const visibleItems = useMemo(() => {
    if (archiveView === "all") return items;
    if (archiveView === "archived") return items.filter((it) => it.is_active === false);
    return items.filter((it) => it.is_active !== false);
  }, [items, archiveView]);

  const visibleIds = useMemo(() => visibleItems.map((it) => it.id), [visibleItems]);
  const visibleSelectedCount = useMemo(
    () => visibleIds.filter((id) => selectedIds.has(id)).length,
    [visibleIds, selectedIds]
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBulk = (mode) => {
    setBulkMode(mode);
    if (mode === "price") setBulkPrice("");
    if (mode === "schedule") setBulkSchedule("all_week");
  };

  const runBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const payload = { item_ids: ids };
    if (bulkMode === "price") {
      const n = parseFloat(bulkPrice);
      if (Number.isNaN(n) || n < 0) {
        toast.error("Enter a valid price");
        return;
      }
      payload.price = n;
    } else if (bulkMode === "schedule") {
      payload.delivery_schedule = bulkSchedule;
    }

    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/menu/bulk-update`, payload);
      toast.success(`Updated ${res.data.modified} item${res.data.modified === 1 ? "" : "s"}`);
      setBulkMode(null);
      clearSelection();
      onBulkUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const runBulkArchive = async (archive) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/menu/bulk-update`, {
        item_ids: ids,
        is_active: !archive,
      });
      toast.success(
        `${archive ? "Archived" : "Restored"} ${res.data.modified} item${
          res.data.modified === 1 ? "" : "s"
        }`
      );
      clearSelection();
      onBulkUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const toggleArchiveOne = async (item) => {
    const archived = item.is_active === false;
    setRowSavingId(item.id);
    try {
      await axios.put(`${API}/menu/${item.id}`, { is_active: archived });
      toast.success(`${item.name} ${archived ? "restored" : "archived"}`);
      onBulkUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setRowSavingId(null);
    }
  };

  const totalSelected = selectedIds.size;

  return (
    <>
      <Button
        variant="outline"
        onClick={downloadLikesMatrixCsv}
        className="w-full mb-3"
        data-testid="export-all-likes-button"
      >
        <Download className="w-4 h-4 mr-2" />
        Export All Likes (CSV)
      </Button>

      {/* Archive view segmented control */}
      <div
        className="inline-flex rounded-lg border overflow-hidden mb-3 text-xs"
        role="tablist"
        data-testid="archive-view-toggle"
      >
        {[
          { key: "active", label: "Active" },
          { key: "archived", label: "Archived" },
          { key: "all", label: "All" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setArchiveView(opt.key)}
            className={`px-3 py-1.5 font-medium transition-colors ${
              archiveView === opt.key
                ? "bg-[#1aa59a] text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            data-testid={`archive-view-${opt.key}`}
            aria-pressed={archiveView === opt.key}
          >
            {opt.label}
            {opt.key === "archived" && (
              <span className="ml-1.5 text-[10px] opacity-80">
                ({items.filter((it) => it.is_active === false).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <Button
          variant={selectedCategoryFilter === null ? "default" : "outline"}
          size="sm"
          onClick={() => onSelectCategoryFilter(null)}
          className={selectedCategoryFilter === null ? "bg-[#1aa59a]" : ""}
        >
          All
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat.key}
            variant={selectedCategoryFilter === cat.key ? "default" : "outline"}
            size="sm"
            onClick={() => onSelectCategoryFilter(cat.key)}
            style={selectedCategoryFilter === cat.key ? { backgroundColor: cat.color } : {}}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      {selectedCategoryFilter && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700">
          Use the arrows to reorder items. Changes save automatically.
        </div>
      )}

      {/* Select-all bar */}
      {!loading && visibleItems.length > 0 && (
        <div className="flex items-center gap-2 mb-2 px-1 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAllVisible}
            className="w-4 h-4 cursor-pointer accent-[#1aa59a]"
            data-testid="bulk-select-all"
            aria-label="Select all visible items"
          />
          <span>
            {totalSelected > 0
              ? `${totalSelected} selected`
              : `Select items for bulk edit`}
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
          {archiveView === "archived"
            ? "No archived items yet."
            : "No items match the current filter."}
        </div>
      ) : (
        <div className="space-y-2 pb-24">
          {visibleItems.map((item, idx) => {
            const checked = selectedIds.has(item.id);
            const archived = item.is_active === false;
            return (
              <Card
                key={item.id}
                className={`overflow-hidden ${archived ? "opacity-60" : ""} ${
                  checked ? "ring-2 ring-[#1aa59a]" : ""
                }`}
              >
                <div className="flex items-center p-2 gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOne(item.id)}
                    className="w-4 h-4 cursor-pointer accent-[#1aa59a] flex-shrink-0"
                    data-testid={`bulk-select-${item.id}`}
                    aria-label={`Select ${item.name}`}
                  />

                  {selectedCategoryFilter && (
                    <div className="flex flex-col gap-0.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onMoveItem(item, "up")}
                        className="h-6 w-6 p-0"
                        disabled={idx === 0 || reordering}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onMoveItem(item, "down")}
                        className="h-6 w-6 p-0"
                        disabled={idx === visibleItems.length - 1 || reordering}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  <div className="w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 relative">
                    <AdminItemImage itemId={item.id} itemName={item.name} />
                    {!item.is_active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <EyeOff className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-sm truncate">{item.name}</h3>
                      {item.week_number && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                          W{item.week_number}
                        </span>
                      )}
                      <ScheduleTag value={item.delivery_schedule} />
                    </div>
                    <span className="text-[#1aa59a] font-bold text-sm">
                      £{item.price.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewLikes(item)}
                      className="h-8 w-8 p-0"
                      title="View likes by location"
                      data-testid={`view-likes-${item.id}`}
                    >
                      <Heart className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditItem(item)}
                      className="h-8 w-8 p-0"
                      title="Edit item"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleArchiveOne(item)}
                      disabled={rowSavingId === item.id}
                      className={`h-8 w-8 p-0 ${archived ? "text-[#1aa59a]" : "text-amber-600"}`}
                      title={archived ? "Restore from archive" : "Archive item (hide from menus)"}
                      data-testid={`${archived ? "restore" : "archive"}-${item.id}`}
                    >
                      {archived ? (
                        <ArchiveRestore className="w-3.5 h-3.5" />
                      ) : (
                        <Archive className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 h-8 w-8 p-0"
                      onClick={() => onDeleteItem(item)}
                      title="Permanently delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sticky bulk action bar */}
      {totalSelected > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t bg-white shadow-2xl"
          data-testid="bulk-action-bar"
        >
          <div className="max-w-3xl mx-auto p-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              title="Clear selection"
              data-testid="bulk-clear"
            >
              <X className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-gray-800">
              {totalSelected} item{totalSelected === 1 ? "" : "s"} selected
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => openBulk("price")}
              data-testid="bulk-set-price"
            >
              Set Price
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openBulk("schedule")}
              data-testid="bulk-set-schedule"
            >
              Set Delivery Schedule
            </Button>
            {archiveView === "archived" ? (
              <Button
                size="sm"
                className="bg-[#1aa59a] hover:bg-[#158a80]"
                onClick={() => runBulkArchive(false)}
                disabled={bulkSaving}
                data-testid="bulk-restore"
              >
                <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                onClick={() => runBulkArchive(true)}
                disabled={bulkSaving}
                data-testid="bulk-archive"
              >
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bulk dialog */}
      <Dialog
        open={bulkMode !== null}
        onOpenChange={(open) => (!open ? setBulkMode(null) : null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {bulkMode === "price"
                ? `Set price on ${totalSelected} item${totalSelected === 1 ? "" : "s"}`
                : `Set delivery schedule on ${totalSelected} item${totalSelected === 1 ? "" : "s"}`}
            </DialogTitle>
          </DialogHeader>
          {bulkMode === "price" ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                This overwrites the base price for every selected item. Per-location overrides
                (Pricing tab) are unchanged.
              </p>
              <Input
                type="number"
                step="0.01"
                min={0}
                inputMode="decimal"
                autoFocus
                placeholder="e.g. 6.50"
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
                data-testid="bulk-price-input"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                Which days can we deliver these items? This drives basket grouping at checkout.
              </p>
              <Select value={bulkSchedule} onValueChange={setBulkSchedule}>
                <SelectTrigger data-testid="bulk-schedule-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_week">All Week (Mon–Sat)</SelectItem>
                  <SelectItem value="mon_wed">Mon–Wed (made early)</SelectItem>
                  <SelectItem value="wed_fri">Wed–Fri (made mid-week)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkMode(null)} disabled={bulkSaving}>
              Cancel
            </Button>
            <Button
              onClick={runBulk}
              disabled={bulkSaving || (bulkMode === "price" && bulkPrice === "")}
              className="bg-[#1aa59a] hover:bg-[#158a80]"
              data-testid="bulk-apply"
            >
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
